import { Util } from "../utils/util";

const GSI_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const GSI_SCRIPT_ID = "google-gsi-client";

/** GIS's own hard cap; a larger value is silently ignored. */
const GSI_MAX_WIDTH = 400;

/**
 * Wrapper over Google Identity Services, using the ID-token flow rather than an OAuth redirect:
 * no callback route to register, and the SPA never navigates away mid-order.
 */
export class GoogleSignInService {
    private readonly _clientId: string;

    /** Static so both modals share one script load, and a startup preload satisfies both. */
    private static _scriptPromise: Promise<boolean> | null = null;

    /**
     * Also static: GIS registers one callback per document, so per-instance state would let the
     * first modal to render own it forever and misroute the other's credential.
     */
    private static _initialized: boolean = false;
    private static _onCredential: ((idToken: string) => void) | null = null;

    private _widthObserver: ResizeObserver | null = null;

    constructor(clientId: string) {
        this._clientId = clientId;
    }

    /** Fetches the script ahead of first use so the button renders with the modal. */
    public static readonly preload = (clientId: string): void => {
        if (clientId && GoogleSignInService.isSupportedHere())
            void new GoogleSignInService(clientId).loadAsync();
    }

    /**
     * Google blocks OAuth inside WebViews, TODO app specific google sign in
     * 85841 remove safari from google sign in/sign out feature
     */
    public static readonly isSupportedHere = (): boolean => !Util.isAppView() && !Util.isiOSSafari();

    public readonly isConfigured = (): boolean =>
        !!this._clientId && GoogleSignInService.isSupportedHere();

    /** Injects the GIS script once; repeat calls share the same promise. */
    public readonly loadAsync = (): Promise<boolean> => {
        if (!this.isConfigured())
            return Promise.resolve(false);

        if (GoogleSignInService._scriptPromise)
            return GoogleSignInService._scriptPromise;

        GoogleSignInService._scriptPromise = new Promise<boolean>(resolve => {
            if (this._hasGoogleApi()) {
                resolve(true);
                return;
            }

            const existing = document.getElementById(GSI_SCRIPT_ID) as HTMLScriptElement | null;
            if (existing) {
                existing.addEventListener("load", () => resolve(this._hasGoogleApi()));
                existing.addEventListener("error", () => resolve(false));
                return;
            }

            const script = document.createElement("script");
            script.id = GSI_SCRIPT_ID;
            script.src = GSI_SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(this._hasGoogleApi());
            script.onerror = () => resolve(false);
            document.head.appendChild(script);
        });

        return GoogleSignInService._scriptPromise;
    }

    /**
     * Renders Google's own branded button into `container` -- their markup, their guidelines.
     */
    public readonly renderButtonAsync = async (
        container: HTMLElement,
        onCredential: (idToken: string) => void,
        options?: { locale?: string, width?: number, text?: "signin_with" | "signup_with" | "continue_with" | "signin" }
    ): Promise<boolean> => {
        const ready = await this.loadAsync();
        if (!ready)
            return false;

        try {
            const google = this._google();

            // Dispatcher reads the static handler, so the credential goes to the last render.
            GoogleSignInService._onCredential = onCredential;

            if (!GoogleSignInService._initialized) {
                google.accounts.id.initialize({
                    client_id: this._clientId,
                    callback: (response: { credential?: string }) => {
                        if (response?.credential)
                            GoogleSignInService._onCredential?.(response.credential);
                    },
                    // Popup keeps the SPA mounted; a redirect would lose the in-progress order.
                    ux_mode: "popup",
                    auto_select: false,
                    cancel_on_tap_outside: true
                });
                GoogleSignInService._initialized = true;
            }

            container.innerHTML = "";
            google.accounts.id.renderButton(container, {
                type: "standard",
                theme: "outline",
                size: "large",
                text: options?.text ?? "signup_with",
                shape: "rectangular",
                logo_alignment: "left",
                locale: options?.locale,
                width: options?.width
            });

            return true;
        } catch {
            // Fail soft: the caller hides the button and manual sign-up still works.
            return false;
        }
    }

    private static readonly _buttonWidth = (host: HTMLElement): number => {
        const available = Math.round(host.getBoundingClientRect().width);
        return available > 0 ? Math.min(available, GSI_MAX_WIDTH) : GSI_MAX_WIDTH;
    }

    /**
     * Re-points GIS's page-wide callback without re-rendering. Callers that skip
     * renderButtonAsync must still call this, or the credential goes to the other modal.
     */
    public readonly claimCredentialHandler = (onCredential: (idToken: string) => void): void => {
        GoogleSignInService._onCredential = onCredential;
    }

    /**
     * Shows or hides a modal's Google section, rendering the button on first use. Returns
     * whether one is mounted; callers keep it so repeat opens take the re-claim path.
     */
    public readonly mountButtonAsync = async (opts: {
        sectionId: string,
        host: HTMLElement | null,
        alreadyRendered: boolean,
        isAvailable: boolean,
        onCredential: (idToken: string) => void,
        locale?: string,
        text?: "signin_with" | "signup_with" | "continue_with" | "signin"
    }): Promise<boolean> => {
        if (!opts.host)
            return false;

        if (!this.isConfigured() || !opts.isAvailable) {
            Util.hideElement(opts.sectionId);
            return false;
        }

        if (opts.alreadyRendered) {
            this.claimCredentialHandler(opts.onCredential);
            Util.showElement(opts.sectionId);
            return true;
        }

        const host = opts.host;
        const rendered = await this.renderButtonAsync(host, opts.onCredential, {
            locale: opts.locale,
            text: opts.text,
            width: GoogleSignInService._buttonWidth(host)
        });

        if (rendered)
            this._observeWidth(host, opts.onCredential, opts.locale, opts.text);

        rendered ? Util.showElement(opts.sectionId) : Util.hideElement(opts.sectionId);
        return rendered;
    }

    /**
     * GIS bakes `width` in at render time, so add observer to rerender on screen size change
     */
    private readonly _observeWidth = (
        host: HTMLElement,
        onCredential: (idToken: string) => void,
        locale?: string,
        text?: "signin_with" | "signup_with" | "continue_with" | "signin"
    ): void => {
        if (this._widthObserver || typeof ResizeObserver === "undefined")
            return;

        let lastWidth = GoogleSignInService._buttonWidth(host);

        this._widthObserver = new ResizeObserver(() => {
            const width = GoogleSignInService._buttonWidth(host);
            if (width === lastWidth)
                return;

            lastWidth = width;
            void this.renderButtonAsync(host, onCredential, { locale, text, width });
        });

        this._widthObserver.observe(host);
    }

    /** Clears GIS's own session hint so the next sign-in shows the account chooser again. */
    public readonly disableAutoSelect = (): void => {
        GoogleSignInService.disableAutoSelect();
    }

    /**
     * Clears GIS's remembered account, so the button stops offering "Sign in as <last user>"
     * after sign-out. Static because the hint is browser-level, not per-instance.
     */
    public static readonly disableAutoSelect = (): void => {
        try {
            //@ts-ignore - GIS attaches itself to window.google
            window.google?.accounts?.id?.disableAutoSelect();
        } catch {
            // Non-fatal: only affects whether Google pre-selects an account next time.
        }
    }

    private readonly _hasGoogleApi = (): boolean => {
        //@ts-ignore - GIS attaches itself to window.google
        return typeof window !== "undefined" && !!window.google?.accounts?.id;
    }

    private readonly _google = (): any => {
        //@ts-ignore - GIS attaches itself to window.google
        return window.google;
    }
}
